/* ===========================================================
   pages/Home.jsx — your docs

   Big "New document" choices (blank + templates), then the list:
   Recent · Starred · Shared with me. Edit copies sit under the doc
   they came from. Works offline from this device's copy.
   Groups (2026-10-28): your named groups sit at the top of Recent,
   open/closed like folders; drag docs onto one. Tick boxes (or
   Ctrl/Shift-click) select several docs for the bar at the bottom.
   =========================================================== */

import { registerContext, findDocInList } from '../ui/context.jsx';
import { useMemo, useState, useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { docs, session, mode, online, pref, updateProfile } from '../state.js';
import { go } from '../lib/router.js';
import { createDoc, trashDoc, renameDoc, starDoc, groups, createGroup, renameGroup, deleteGroup, moveToGroup } from '../sync/docs.js';
import { TEMPLATES } from '../templates.js';
import { Icon, IconButton, Button, Menu, Avatar, promptDialog, confirmDialog, timeAgo, toast } from '../ui/ui.jsx';
import { Proto } from '../ui/proto.jsx';
import { Logo } from './DocPage.jsx';
import { signOut } from './Gate.jsx';
import { w } from '../lib/words.js';
import { openApp, appHref } from '../lib/hosts.js';
import { NotesSide } from './NotesDrive.jsx';

/* The Updates doc: the one picked before (prefs.updatesDoc), else the doc called
   "Protocol updates", else a new one. Claude reads it and replies in its Claude tab. */
function updatesId() {
  const mine = docs.value.filter(d => d.role === 'owner' && !d.deletedAt);
  const id = pref('updatesDoc', null);
  return mine.some(d => d.id === id) ? id : mine.find(d => /^protocol updates$/i.test((d.title || '').trim()))?.id || null;
}

async function openUpdates() {
  let id = updatesId();
  if (!id) id = await createDoc({ title: 'Protocol updates' });
  if (id !== pref('updatesDoc', null)) updateProfile({ prefs: { updatesDoc: id } }).catch(() => {});
  go(`/d/${id}`);
}

export function TopBar({ title }) {
  const s = session.value;
  return (
    <header class="top">
      <a href={appHref('docs')} class="brand" aria-label="Docs Protocol home" onClick={e => { e.preventDefault(); openApp('docs'); }}><Logo /><span>Docs Protocol</span></a>
      {title && <h1 class="top-title">{title}</h1>}
      <button type="button" class="searchbox" onClick={() => window.dispatchEvent(new CustomEvent('dp:palette'))} aria-label="Search docs and actions (Ctrl K)">
        <Icon name="search" size={17} /><span>Search</span><kbd class="hide-sm">Ctrl K</kbd>
      </button>
      <Button size="sm" kind="ghost" icon="pencil" onClick={() => openApp('notes')}>Notes</Button>
      <Button size="sm" kind="ghost" icon="folder" onClick={() => openApp('drive')}>Drive</Button>
      {s.owner && <Button size="sm" icon="sparkle" class="updates-btn" onClick={openUpdates}>Updates</Button>}
      {!online.value && <span class="status s-offline"><Icon name="cloudOff" size={15} /><span class="status-text">Offline</span></span>}
      <Menu align="right" label="Account" trigger={<button type="button" class="avatar-btn" aria-label="Account menu"><Avatar name={s.profile?.name} seed={s.user?.id} size={32} /></button>}
        items={[
          { heading: s.user?.email },
          // his Updates doc (his ask): only on the owner's account, at the top
          { label: 'Notes', icon: 'pencil', onSelect: () => openApp('notes') },
          { label: 'Drive', icon: 'folder', onSelect: () => openApp('drive') },
          { label: 'Settings', icon: 'settings', onSelect: () => go('/settings') },
          { label: 'Open from computer', icon: 'computer', onSelect: () => go('/files') },
          { label: 'Trash', icon: 'trash', onSelect: () => go('/trash') },
          { label: 'Welcome tour', icon: 'sparkle', onSelect: () => go('/welcome') },
          'sep',
          { label: 'Sign out', icon: 'logout', onSelect: signOut },
        ]} />
    </header>
  );
}

export function Home({ filter: initial = 'recent' }) {
  const [filter, setFilter] = useState(initial);
  const dev = mode.value === 'dev';
  // notes and Drive files have their own pages
  // notes and Drive files have their own pages; the Updates doc has its own button
  const upd = session.value.owner ? updatesId() : null;
  const all = docs.value.filter(d => !d.deletedAt && !d.local && d.kind !== 'note' && d.kind !== 'file' && d.id !== upd);

  const shown = useMemo(() => {
    let list = all;
    if (filter === 'starred') list = list.filter(d => d.starred);
    if (filter === 'shared') list = list.filter(d => d.role !== 'owner');
    // edit copies go under their original when it's in the list
    const ids = new Set(list.map(d => d.id));
    const top = list.filter(d => !(d.kind === 'copy' && ids.has(d.parentId)));
    return top.map(d => ({ doc: d, copies: list.filter(c => c.kind === 'copy' && c.parentId === d.id) }));
  }, [docs.value, filter]);
  // the order rows appear in, for Shift-click ranges
  const order = useMemo(() => shown.flatMap(x => [x.doc.id, ...x.copies.map(c => c.id)]), [shown]);
  // changing the tab clears the selection
  useEffect(() => { selected.value = new Set(); }, [filter]);

  const newDoc = async tpl => {
    const id = await createDoc({ title: tpl.id === 'blank' ? '' : tpl.name, json: tpl.json });
    go(`/d/${id}`);
  };

  return (
    <div class="home">
      <TopBar />
      <div class="with-notes">
      <main id="main" class="home-main">
        <section class="new-row" aria-label="Start a new document">
          <h2 class="section-title">Start something new</h2>
          <div class="templates">
            {TEMPLATES.filter(t => !t.dev || dev).map(t => (
              <button type="button" class={`template ${t.id === 'blank' ? 'blank' : ''}`} onClick={() => newDoc(t)}>
                <span class="template-page"><Icon name={t.id === 'blank' ? 'plus' : t.icon} size={t.id === 'blank' ? 30 : 22} /></span>
                <span class="template-name">{t.id === 'blank' ? 'Blank document' : t.name}</span>
              </button>
            ))}
            <button type="button" class="template" onClick={() => go('/files')}>
              <span class="template-page"><Icon name="computer" size={22} /></span>
              <span class="template-name">Open from computer</span>
            </button>
          </div>
        </section>

        <section aria-label="Your documents">
          <div class="list-head">
            <h2 class="section-title">Your docs</h2>
            <Button size="sm" icon="folder" onClick={async () => {
              const name = await promptDialog({ title: 'New group', label: 'Name', value: '' });
              if (name !== null) { const g = await createGroup(name); openGroups.value = new Set([...openGroups.value, g.id]); saveOpen(); }
            }}>New group</Button>
            <div class="segmented" role="tablist" aria-label="Show">
              {[['recent', 'Recent'], ['starred', 'Starred'], ['shared', 'Shared with me']].map(([k, label]) => (
                <button type="button" role="tab" aria-selected={String(filter === k)} class={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{label}</button>
              ))}
            </div>
          </div>
          {!shown.length && !(filter === 'recent' && groups.value.length) ? <Empty filter={filter} /> : (
            <ul class="doclist" onDragOver={e => { if (e.dataTransfer.types.includes('text/x-dp-doc') && !e.target.closest('.grouprow')) e.preventDefault(); }}
              onDrop={e => {
                if (e.target.closest('.grouprow')) return;
                const ids = JSON.parse(e.dataTransfer.getData('text/x-dp-doc') || '[]').filter(id => docs.value.find(d => d.id === id)?.groupId);
                if (ids.length) { e.preventDefault(); moveToGroup(ids, null).then(() => toast('Taken out of the group.')); }
              }}>
              {filter === 'recent' && groups.value.map(g => {
                const inG = shown.filter(x => x.doc.groupId === g.id);
                const isOpen = openGroups.value.has(g.id);
                return (
                  <>
                    <GroupRow group={g} count={inG.length} open={isOpen} key={`g${g.id}`} />
                    {isOpen && inG.map(({ doc, copies }) => (
                      <>
                        <DocRow doc={doc} key={doc.id} order={order} inGroup />
                        {copies.map(c => <DocRow doc={c} key={c.id} order={order} nested />)}
                      </>
                    ))}
                    {isOpen && !inG.length && <li class="group-empty" key={`ge${g.id}`}>Empty. Drag docs here, or tick some and use "Move to group".</li>}
                  </>
                );
              })}
              {(filter === 'recent' ? shown.filter(x => !groups.value.some(g => g.id === x.doc.groupId)) : shown).map(({ doc, copies }) => (
                <>
                  <DocRow doc={doc} key={doc.id} order={order} />
                  {copies.map(c => <DocRow doc={c} key={c.id} order={order} nested />)}
                </>
              ))}
            </ul>
          )}
          <SelectionBar />
        </section>
      </main>
      <NotesSide />
      </div>
    </div>
  );
}

function Empty({ filter }) {
  const text = filter === 'starred' ? 'No starred docs yet. Star one from its menu to keep it handy.'
    : filter === 'shared' ? 'Nothing shared with you yet. When someone sends you a link, the doc shows up here.'
      : 'No docs yet. Pick "Blank document" above to start writing.';
  return <div class="empty"><Proto size={64} /><p>{text}</p></div>;
}

/** A doc's options: the ⋮ menu on its row and the right-click menu use the same list. */
export function rowItems(doc) {
  if (!doc) return null;
  const open = () => go(`/d/${doc.id}`);
  const owner = doc.role === 'owner';
  return [
    { label: 'Open', icon: 'open', onSelect: open },
    { label: 'Open in a new tab', icon: 'globe', onSelect: () => window.open(`/d/${doc.id}`, '_blank') },
    owner && { label: 'Rename', icon: 'pencil', onSelect: async () => {
      const t = await promptDialog({ title: 'Rename', label: 'Title', value: doc.title });
      if (t !== null) renameDoc(doc.id, t.trim()).then(() => toast('Renamed.'));
    } },
    { label: doc.starred ? 'Remove star' : 'Star', icon: 'star', onSelect: () => starDoc(doc.id, !doc.starred) },
    ...groups.value.filter(g => g.id !== doc.groupId).slice(0, 8).map(g => ({ label: `Move to ${g.name}`, icon: 'folder', onSelect: () => moveToGroup([doc.id], g.id) })),
    doc.groupId && { label: 'Take out of group', icon: 'close', onSelect: () => moveToGroup([doc.id], null) },
    'sep',
    { label: owner ? 'Move to trash' : 'Remove from my docs', icon: 'trash', danger: true, onSelect: async () => {
      if (!await confirmDialog({ title: owner ? 'Move to trash?' : 'Remove from your docs?', text: owner ? 'You can restore it from the trash for 30 days.' : 'The owner still has it.', ok: owner ? 'Move to trash' : 'Remove', danger: true })) return;
      await trashDoc(doc.id);
      toast(owner ? 'Moved to trash.' : 'Removed.', { action: owner ? { label: 'Undo', run: () => import('../sync/docs.js').then(m => m.restoreDoc(doc.id)) } : undefined });
    } },
  ];
}
registerContext('.docrow[data-doc]', el => rowItems(findDocInList(el.dataset.doc)));

/* ---------- selecting several docs ---------- */

const selected = signal(new Set());
let lastPicked = null;

function pick(id, e, order) {
  const s = new Set(selected.value);
  if (e?.shiftKey && lastPicked && order) {
    const a = order.indexOf(lastPicked), b = order.indexOf(id);
    if (a >= 0 && b >= 0) for (const x of order.slice(Math.min(a, b), Math.max(a, b) + 1)) s.add(x);
  } else if (s.has(id)) s.delete(id);
  else s.add(id);
  lastPicked = id;
  selected.value = s;
}

function SelectionBar() {
  const ids = [...selected.value];
  if (!ids.length) return null;
  const list = docs.value.filter(d => ids.includes(d.id));
  const allStarred = list.every(d => d.starred);
  const owned = list.filter(d => d.role === 'owner');
  const clear = () => { selected.value = new Set(); };
  return (
    <div class="selbar" role="toolbar" aria-label="Selected docs">
      <b>{ids.length} selected</b>
      <Menu label="Move to group" trigger={<Button size="sm" icon="folder">Move to group</Button>} items={[
        ...groups.value.map(g => ({ label: g.name, icon: 'folder', onSelect: () => moveToGroup(ids, g.id).then(() => { toast(`Moved to ${g.name}.`); clear(); }) })),
        groups.value.length > 0 && 'sep',
        { label: 'New group…', icon: 'plus', onSelect: async () => {
          const name = await promptDialog({ title: 'New group', label: 'Name', value: '' });
          if (name === null) return;
          const g = await createGroup(name);
          await moveToGroup(ids, g.id);
          openGroups.value = new Set([...openGroups.value, g.id]); saveOpen();
          toast(`Moved to ${g.name}.`); clear();
        } },
        list.some(d => d.groupId) && { label: 'Take out of its group', icon: 'close', onSelect: () => moveToGroup(ids, null).then(clear) },
      ]} />
      <Button size="sm" icon="star" onClick={() => { for (const d of list) starDoc(d.id, !allStarred); clear(); }}>{allStarred ? 'Unstar' : 'Star'}</Button>
      <Button size="sm" icon="trash" kind="danger-plain" onClick={async () => {
        if (!await confirmDialog({ title: `Move ${ids.length} to trash?`, text: owned.length < list.length ? 'Docs shared with you are only removed from your list; the owner keeps them.' : 'You can restore them from the trash for 30 days.', ok: 'Move to trash', danger: true })) return;
        for (const d of list) await trashDoc(d.id);
        toast(`${ids.length} moved to trash.`); clear();
      }}>Trash</Button>
      <span class="grow" />
      <Button size="sm" kind="ghost" onClick={clear}>Clear</Button>
    </div>
  );
}

/* ---------- groups ---------- */

const OKEY = 'dp:groupsOpen';
const openGroups = signal(new Set((() => { try { return JSON.parse(localStorage.getItem(OKEY) || '[]'); } catch { return []; } })()));
const saveOpen = () => { try { localStorage.setItem(OKEY, JSON.stringify([...openGroups.value])); } catch { /* fine */ } };
const toggleGroup = id => { const s = new Set(openGroups.value); if (s.has(id)) s.delete(id); else s.add(id); openGroups.value = s; saveOpen(); };

function groupItems(g) {
  return [
    { label: 'New doc in this group', icon: 'plus', onSelect: async () => { const id = await createDoc({}); await moveToGroup([id], g.id); go(`/d/${id}`); } },
    { label: 'Rename group', icon: 'pencil', onSelect: async () => {
      const name = await promptDialog({ title: 'Rename group', label: 'Name', value: g.name });
      if (name?.trim()) renameGroup(g.id, name.trim().slice(0, 60));
    } },
    'sep',
    { label: 'Delete group', icon: 'trash', danger: true, onSelect: async () => {
      if (!await confirmDialog({ title: `Delete "${g.name}"?`, text: 'Only the group goes. Its docs stay in your list.', ok: 'Delete group', danger: true })) return;
      deleteGroup(g.id);
    } },
  ];
}
registerContext('.grouprow[data-group]', el => { const g = groups.value.find(x => x.id === el.dataset.group); return g ? groupItems(g) : null; });

function GroupRow({ group, count, open }) {
  const [over, setOver] = useState(false);
  return (
    <li class={`grouprow ${over ? 'drop' : ''}`} data-group={group.id}
      onDragOver={e => { if (e.dataTransfer.types.includes('text/x-dp-doc')) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        e.preventDefault(); setOver(false);
        const ids = JSON.parse(e.dataTransfer.getData('text/x-dp-doc') || '[]');
        if (ids.length) moveToGroup(ids, group.id).then(() => { toast(`Moved to ${group.name}.`); selected.value = new Set(); });
      }}>
      <button type="button" class="grouprow-main" aria-expanded={String(open)} onClick={() => toggleGroup(group.id)}>
        <Icon name="chevron" size={16} class={`group-chev ${open ? 'open' : ''}`} />
        <Icon name="folder" size={20} />
        <span class="grouprow-name">{group.name}</span>
        <span class="grouprow-count">{count} doc{count === 1 ? '' : 's'}</span>
      </button>
      <Menu align="right" label={`Options for ${group.name}`} trigger={<IconButton icon="more" label="Group options" />} items={groupItems(group)} />
    </li>
  );
}

function DocRow({ doc, nested, inGroup, order }) {
  const open = () => go(`/d/${doc.id}`);
  const isSel = selected.value.has(doc.id);
  const anySel = selected.value.size > 0;
  return (
    <li class={`docrow ${nested ? 'nested' : ''} ${inGroup ? 'in-group' : ''} ${isSel ? 'sel' : ''} ${anySel ? 'selecting' : ''}`} data-doc={doc.id}
      draggable="true"
      onDragStart={e => {
        const ids = isSel ? [...selected.value] : [doc.id];
        e.dataTransfer.setData('text/x-dp-doc', JSON.stringify(ids));
        e.dataTransfer.effectAllowed = 'move';
      }}>
      <label class="docrow-check" title="Select" onClick={e => { e.stopPropagation(); if (e.target.tagName !== 'INPUT') { e.preventDefault(); pick(doc.id, e, order); } }}>
        <input type="checkbox" checked={isSel} aria-label={`Select ${doc.title || 'Untitled document'}`}
          onClick={e => pick(doc.id, e, order)} onChange={() => {}} />
      </label>
      <a href={`/d/${doc.id}`} class="docrow-main" onClick={e => {
        e.preventDefault();
        // Ctrl/Cmd or Shift picks instead of opening; while picking, a click picks too
        if (e.ctrlKey || e.metaKey || e.shiftKey || anySel) { pick(doc.id, e, order); return; }
        open();
      }}>
        <span class="docrow-icon"><Icon name={doc.kind === 'copy' ? 'branch' : 'doc'} size={20} /></span>
        <span class="docrow-text">
          <span class="docrow-title">
            {doc.title || 'Untitled document'}
            {doc.kind === 'copy' && <span class="tag accent">{w('copy')}</span>}
            {doc.pendingCreate && <span class="tag" title="Made offline; it uploads when you're online">On this device</span>}
          </span>
          <span class="docrow-snippet">{doc.snippet || 'Empty'}</span>
        </span>
        <span class="docrow-meta">
          {doc.role !== 'owner' && <span class="docrow-owner"><Icon name="users" size={14} /> {doc.ownerName || 'Shared'}</span>}
          <span>{timeAgo(doc.updatedAt)}</span>
        </span>
      </a>
      {doc.starred && <Icon name="star" size={15} class="star-on" />}
      <Menu align="right" label={`Options for ${doc.title || 'Untitled document'}`} trigger={<IconButton icon="more" label="Options" />}
        items={rowItems(doc)} />
    </li>
  );
}
